// Turn an object into a Markdown document with a YAML frontmatter block for its
// metadata, and trigger a browser download. The frontmatter format mirrors what
// document-editor.tsx's parseFrontmatter reads (bare scalars, `- item` arrays),
// so a downloaded file round-trips back into the preview.

type MarkdownOptions = {
	/** Rendered as a top-level `# heading` above the body. */
	title?: string;
	/** Explicit Markdown body. Takes precedence over `bodyKey`. */
	body?: string;
	/** Field on the object whose (string) value is used as the body. */
	bodyKey?: string;
	/** Fields to keep out of the frontmatter (e.g. nested arrays, `__typename`). */
	exclude?: string[];
};

function scalar(value: unknown): string {
	if (value === null || value === undefined) return "";
	// Nested objects/arrays have no clean single-line YAML form here — inline JSON
	// stays valid and readable enough for the odd metadata field.
	if (typeof value === "object") return JSON.stringify(value);
	return String(value).replace(/\r?\n/g, " ").trim();
}

function toFrontmatter(meta: Record<string, unknown>): string {
	const lines: string[] = [];
	for (const [key, value] of Object.entries(meta)) {
		if (value === undefined || value === null || value === "") continue;
		if (Array.isArray(value)) {
			if (value.length === 0) continue;
			lines.push(`${key}:`);
			for (const item of value) lines.push(`  - ${scalar(item)}`);
		} else {
			lines.push(`${key}: ${scalar(value)}`);
		}
	}
	return lines.join("\n");
}

/** URL/filename-safe slug from an arbitrary string. */
export function slugify(text: string): string {
	return (
		text
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "")
			.slice(0, 60) || "export"
	);
}

/** Serialize an object to Markdown: `--- frontmatter ---` then an optional heading + body. */
export function objectToMarkdown(
	obj: Record<string, unknown>,
	opts: MarkdownOptions = {},
): string {
	const skip = new Set([
		...(opts.exclude ?? []),
		...(opts.bodyKey ? [opts.bodyKey] : []),
	]);
	const meta: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(obj)) {
		if (skip.has(key) || typeof value === "function") continue;
		meta[key] = value;
	}

	const body =
		opts.body ??
		(opts.bodyKey && typeof obj[opts.bodyKey] === "string"
			? (obj[opts.bodyKey] as string)
			: "");
	const heading = opts.title ? `# ${opts.title}\n\n` : "";

	return `${`---\n${toFrontmatter(meta)}\n---\n\n${heading}${body}`.trimEnd()}\n`;
}

/** Download arbitrary text as a `.md` file. */
export function downloadMarkdown(filename: string, markdown: string): void {
	const name = filename.endsWith(".md") ? filename : `${filename}.md`;
	const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = name;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
}

/** Serialize an object to Markdown and download it in one call. */
export function downloadObjectAsMarkdown(
	obj: Record<string, unknown>,
	filename: string,
	opts: MarkdownOptions = {},
): void {
	downloadMarkdown(filename, objectToMarkdown(obj, opts));
}
